import { isFeatureFlagEnabled } from '@ipfabric/ui-components/utils/featureFlags';
import type { TableColumn } from '@ipfabric/utils';
import { getValueFromKeyOfType } from '@ipfabric/utils';
import { AimCalculateDevicesButton } from 'components/ApiTablePage/actionsColumns/AimCalculateDevicesButton';
import { AimCalculateDevicesLink } from 'components/ApiTablePage/actionsColumns/AimCalculateDevicesLink';
import { AimCalculatedLabel } from 'components/ApiTablePage/actionsColumns/AimCalculatedLabel';
import { AimTableValueWithActionCell } from 'components/ApiTablePage/actionsColumns/AimTableValueWithActionCell';
import type { AimFlowPathLookupBody } from 'components/ApiTablePage/actionsColumns/useCalculateDevices';
import { maybeWrapWithLink } from 'components/Table/cells/helpers/maybeWrapWithLink';
import { renderCellContentByType } from 'components/Table/cells/renderCellContentByType';
import type { RenderCellContentArguments } from 'components/Table/model/TableCellContentRenderFunction';
import type { ReactNode } from 'react';
import { distinct } from 'utils/array/distinct';

/** Room for the value, the divider and the button without the header clipping. */
const FLOWS_CALCULATED_COLUMN_WIDTH = 130;

/** Shown instead of a count that has not been calculated — distinct from a real 0. */
const NOT_CALCULATED = '-';

/**
 * Render the column exactly as it normally would. `renderContent` is the first
 * branch of `renderCellContentByType`, so it has to be stripped or the base
 * render would recurse into the override.
 */
const renderBaseCell = <TRow,>(
  column: TableColumn<any>,
  args: RenderCellContentArguments<TRow>,
) => renderCellContentByType({ ...column, renderContent: undefined }, args);

/**
 * Wrap arbitrary content in the column's own link, for cells that rebuild their
 * content and so lose the link the base render put on part of it. Goes through
 * the shared wrapper, so the column's `icon: false` is honoured.
 */
const wrapWithColumnLink = <TRow,>(
  column: TableColumn<any>,
  args: RenderCellContentArguments<TRow>,
  node: ReactNode,
): ReactNode =>
  maybeWrapWithLink(
    args.row,
    args.severity !== null,
    'createLinkProps' in column.format
      ? column.format.createLinkProps
      : undefined,
    !args.context.disableLinks,
  )(node);

type ApplicationRow = {
  id: string;
  name: string | null;
  flows: number | null;
  flowsCalculated: number | null;
};

/**
 * Deep-link to the Devices tab filtered to the application's devices — the same
 * filter the `devices` column link uses in `applications.yaml`. The Devices
 * table has no single `applicationName`; a device is reached via a flow whose
 * src OR dst workload belongs to the app, so match either side. Relative path
 * only, to satisfy the endpoint's open-redirect guard on `callbackUrl`.
 */
const buildDevicesCallbackUrl = (applicationName: string): string =>
  `/inventory/applications/devices?options=${encodeURIComponent(
    JSON.stringify({
      filters: {
        or: [
          { srcApplicationName: ['eq', applicationName] },
          { dstApplicationName: ['eq', applicationName] },
        ],
      },
    }),
  )}`;

const applicationCalculateBody = (
  row: ApplicationRow,
): AimFlowPathLookupBody => ({
  applicationId: row.id,
  callbackUrl: row.name ? buildDevicesCallbackUrl(row.name) : undefined,
});

/**
 * Why the application has nothing left to calculate, or `undefined` while it
 * still has flows to work through.
 *
 * Mirrors the Flows tab, where a calculated flow shows no trigger at all: within
 * a snapshot a calculated result cannot go stale, so recalculating is a no-op.
 */
const calculateUnavailableReason = (
  calculated: number | null,
  total: number | null,
): string | undefined => {
  if (!total) {
    return 'This application has no flows to calculate.';
  }

  return (calculated ?? 0) >= total
    ? "Path-lookups for all the application's flows have been calculated."
    : undefined;
};

/**
 * Applications `Flows calculated`: `{calculated}/{total}` beside the refresh
 * trigger.
 *
 * The whole fraction is one link to the application's flows, not just the
 * calculated half, and it links whatever the counts are. The link filters the
 * Flows tab by application name alone — nothing in it references calculation
 * status — so with `0/{total}` it lands on exactly the flows still waiting to be
 * calculated. A zero linking is also what every other count column in this table
 * does; `renderNumberCell` withholds a link only from an empty value.
 *
 * The `flows` column is `disabledUI` in the OAS precisely so its value can be
 * folded in here rather than occupying a column of its own.
 */
export const aimApplicationsFlowsCalculatedColumnOverride = (
  column: TableColumn<any>,
): TableColumn<any> => ({
  ...column,
  width: Math.max(column.width ?? 0, FLOWS_CALCULATED_COLUMN_WIDTH),
  webDependencies: distinct([...column.webDependencies, 'id', 'name', 'flows']),
  renderContent: (args: RenderCellContentArguments<ApplicationRow>) => {
    const cell = renderBaseCell(column, args);
    const calculated = getValueFromKeyOfType<ApplicationRow, number | null>(
      args.row,
      'flowsCalculated',
    );
    const total = getValueFromKeyOfType<ApplicationRow, number | null>(
      args.row,
      'flows',
    );
    const fraction = `${calculated ?? 0}/${total ?? 0}`;

    return {
      ...cell,
      alignment: 'right',
      content: (
        <AimTableValueWithActionCell
          value={wrapWithColumnLink(column, args, fraction)}
          action={
            <AimCalculateDevicesButton
              body={applicationCalculateBody(args.row)}
              unavailableReason={calculateUnavailableReason(calculated, total)}
            />
          }
        />
      ),
    };
  },
});

/**
 * Applications `Name`: link the application name to its detail page
 * (`/inventory/applications/<id>`).
 */
export const aimApplicationsNameColumnOverride = (
  column: TableColumn<any>,
): TableColumn<any> => {
  if (
    !isFeatureFlagEnabled('ENABLE_AIM_APPLICATION_DETAIL') ||
    column.format.type !== 'text'
  ) {
    return column;
  }

  return {
    ...column,
    webDependencies: distinct([...column.webDependencies, 'id']),
    format: {
      ...column.format,
      createLinkProps: (row: ApplicationRow) =>
        row.id
          ? {
              to: `/inventory/applications/${encodeURIComponent(row.id)}`,
              hideIcon: true,
            }
          : undefined,
    },
  };
};

/**
 * A device count that has to distinguish "not calculated" from a genuine zero:
 * `-` for the former, otherwise the linked count the column already renders.
 *
 * Used by both the Applications and the Flows `Devices` columns. Neither carries
 * an action — each row has a single trigger, in `Flows calculated` and
 * `Calculation status` respectively.
 */
export const aimDeviceCountColumnOverride = (
  column: TableColumn<any>,
): TableColumn<any> => ({
  ...column,
  renderContent: (
    args: RenderCellContentArguments<{ devices: number | null }>,
  ) => {
    const cell = renderBaseCell(column, args);
    const devices = getValueFromKeyOfType<
      { devices: number | null },
      number | null
    >(args.row, 'devices');

    return devices === null
      ? { ...cell, alignment: 'right', content: NOT_CALCULATED }
      : cell;
  },
});

/**
 * Flows `Calculation status`: the word `Calculated`, or the trigger that makes it so.
 * Never both — a calculated flow cannot go stale, because topology changes
 * arrive only with a new snapshot, which starts with nothing calculated.
 *
 * The count lives in the neighbouring `Devices` column, so this cell carries no
 * number.
 */
export const aimFlowsCalculationStatusColumnOverride = (
  column: TableColumn<any>,
): TableColumn<any> => ({
  ...column,
  webDependencies: distinct([...column.webDependencies, 'id']),
  renderContent: (
    args: RenderCellContentArguments<{
      id: string;
      pathLookupsCalculationStatus: boolean | null;
    }>,
  ) => {
    const cell = renderBaseCell(column, args);
    const calculated = getValueFromKeyOfType<
      { pathLookupsCalculationStatus: boolean | null },
      boolean | null
    >(args.row, 'pathLookupsCalculationStatus');

    return {
      ...cell,
      alignment: 'left',
      content: calculated ? (
        <AimCalculatedLabel />
      ) : (
        <AimCalculateDevicesLink body={{ flowId: args.row.id }} />
      ),
    };
  },
});
